"use client";

import Link from "next/link";

import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

function generatePagination(currentPage: number, totalPages: number) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "…", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      "…",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    "…",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "…",
    totalPages,
  ];
}

export default function Pagination({
  totalPages,
  current = "1",
  path,
}: {
  totalPages: number;
  current: string | number;
  path: string;
}) {
  const currentPage = Number(current) || 1;
  const createPageURL = (pageNumber: number | string) => {
    if (pageNumber === 1) {
      return path;
    }
    return `${path}/${pageNumber.toString()}`;
  };

  return (
    <div
      data-test-id="pagination"
      className="flex items-center justify-center space-x-4"
    >
      {currentPage > 1 && (
        <Link
          href={createPageURL(currentPage - 1)}
          className={clsx(
            "flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300",
          )}
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </Link>
      )}
      {generatePagination(currentPage, totalPages).map((page, index) => {
        if (page === "…") {
          return (
            <span
              key={index}
              className="flex h-10 w-10 items-center justify-center"
            >
              {page}
            </span>
          );
        }
        return (
          <Link
            key={index}
            href={createPageURL(page)}
            className={clsx(
              "h-10 w-10 items-center justify-center rounded-full hover:bg-gray-300 md:flex",
              currentPage === page
                ? "flex bg-gray-800 text-white"
                : "hidden bg-gray-200",
            )}
          >
            {page}
          </Link>
        );
      })}
      {currentPage < totalPages && (
        <Link
          href={createPageURL(currentPage + 1)}
          className={clsx(
            "flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300",
          )}
        >
          <ArrowRightIcon className="h-6 w-6" />
        </Link>
      )}
    </div>
  );
}
